import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { PushNotifications, Token, ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { clearAppIconBadge } from '@/services/app-icon-badge';
import { FcmPush } from '@/services/fcm-push.plugin';
import { HmsPush } from '@/services/hms-push.plugin';
import { notifyMessengerLocalEvent } from '@/services/messenger-realtime.service';
import { refreshMessengerUnread } from '@/services/messenger-unread';
import router from '@/router';
import store from '@/store';

export { clearAppIconBadge } from '@/services/app-icon-badge';

/** Listeners attach once per app lifecycle; each login re-runs registration to re-bind the token. */
let fcmListenersAttached = false;
let iosFcmListenersAttached = false;
let hmsListenersAttached = false;
let messengerPushListenersAttached = false;
let earlyHandlersAttached = false;
let registerInFlight: Promise<void> | null = null;
/** Last token successfully accepted by API in this JS session (avoid spam on resume). */
let lastSyncedTokenKey: string | null = null;

type PendingOpen =
  | { kind: 'chat'; chatId: number }
  | { kind: 'inbox' };

let pendingOpen: PendingOpen | null = null;

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

function pickChatId(data: Record<string, unknown>): number | null {
  const raw =
    data.chatId ??
    data.chat_id ??
    asRecord(data.data)?.chatId ??
    asRecord(data.data)?.chat_id;
  const chatId = Number(raw);
  // SYSTEM-чат засеян с отрицательным id (-700001).
  if (Number.isFinite(chatId) && chatId !== 0) return chatId;
  return null;
}

function isMessengerPush(data: Record<string, unknown>): boolean {
  const type = String(data.type || data.notificationType || asRecord(data.data)?.type || '');
  const route = String(data.route || asRecord(data.data)?.route || '');
  return type === 'MESSENGER' || route === 'messenger-chat' || data.chatId != null || data.chat_id != null;
}

function canNavigateNow(): boolean {
  return !!store.getters.authenticated;
}

function navigateToChat(chatId: number): void {
  void router
    .push({ name: 'MessengerChat', params: { id: String(chatId) } })
    .catch(() => undefined)
    .finally(() => {
      // Если чат уже был открыт, Ionic может не пересоздать страницу — форсируем reload.
      notifyMessengerLocalEvent({ type: 'CHAT_REFRESH', chatId });
    });
}

function navigateToInbox(): void {
  void router.push({ name: 'MessengerInbox' }).catch(() => {
    void router.push({ path: '/messenger' });
  });
}

export function openMessengerFromPushData(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  const flat = { ...data, ...(asRecord(data.data) || {}) };
  const chatId = pickChatId(flat);
  const messenger = isMessengerPush(flat);

  if (chatId != null) {
    if (canNavigateNow()) {
      navigateToChat(chatId);
    } else {
      pendingOpen = { kind: 'chat', chatId };
    }
    void clearAppIconBadge();
    return;
  }

  if (messenger) {
    if (canNavigateNow()) {
      navigateToInbox();
    } else {
      pendingOpen = { kind: 'inbox' };
    }
    void clearAppIconBadge();
  }
}

/** Call after login / session restore so a cold-start tap can open the chat. */
export function flushPendingPushOpen(): void {
  if (!pendingOpen) return;
  if (!canNavigateNow()) return;
  const next = pendingOpen;
  pendingOpen = null;
  if (next.kind === 'chat') {
    navigateToChat(next.chatId);
  } else {
    navigateToInbox();
  }
}

/**
 * Attach open handlers as early as possible (before auth), so cold-start taps are not lost.
 */
export async function attachPushOpenHandlersEarly(): Promise<void> {
  if (earlyHandlersAttached || !Capacitor.isNativePlatform()) return;
  earlyHandlersAttached = true;
  messengerPushListenersAttached = true;

  await PushNotifications.addListener('pushNotificationActionPerformed', (event: ActionPerformed) => {
    openMessengerFromPushData((event.notification?.data || {}) as Record<string, unknown>);
  });
  await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    // WS в фоне часто мёртв — обновляем бейдж по факту пуша.
    void refreshMessengerUnread().then((total) => {
      if (total <= 0) void clearAppIconBadge();
    });
    const chatId = pickChatId((notification?.data || {}) as Record<string, unknown>);
    if (chatId != null) {
      notifyMessengerLocalEvent({ type: 'CHAT_REFRESH', chatId });
    }
  });

  if (Capacitor.getPlatform() === 'ios') {
    await FcmPush.addListener('notificationOpened', (event) => {
      openMessengerFromPushData((event?.data || {}) as Record<string, unknown>);
    });
    try {
      const launch = await FcmPush.consumeLaunchNotification();
      if (launch?.data && typeof launch.data === 'object') {
        openMessengerFromPushData(launch.data as Record<string, unknown>);
      }
    } catch {
      /* ignore */
    }
  }

  // HMS (Huawei) taps arrive via custom plugin intent extras, not Capacitor PushNotifications.
  try {
    await HmsPush.addListener('notificationOpened', (event) => {
      openMessengerFromPushData((event?.data || {}) as Record<string, unknown>);
    });
  } catch {
    /* play flavor stub may not support the event */
  }
}

async function ensureMessengerPushOpenHandlers(): Promise<void> {
  await attachPushOpenHandlersEarly();
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function sendToken(provider: 'FCM' | 'HMS', token: string): Promise<boolean> {
  const trimmed = token?.trim();
  if (!trimmed) {
    console.warn('[push] empty token, skip upload', provider);
    return false;
  }

  const tokenKey = `${provider}:${trimmed}`;
  if (tokenKey === lastSyncedTokenKey) {
    return true;
  }

  let jwt = (await Preferences.get({ key: 'jwt' })).value;
  if (!jwt?.trim()) {
    // Короткий retry: login иногда вызывает register до того, как Preferences.set завершится.
    for (let i = 0; i < 5 && !jwt?.trim(); i++) {
      await sleep(150 * (i + 1));
      jwt = (await Preferences.get({ key: 'jwt' })).value;
    }
  }
  if (!jwt?.trim()) {
    console.warn('[push] no JWT in Preferences, skip token upload', provider);
    return false;
  }

  const maxAttempts = 3;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const res = await CapacitorHttp.post({
        url: `${process.env.VUE_APP_API_URL}api/account/push-device`,
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        data: { provider, token: trimmed },
        readTimeout: 15000,
      });
      const status = res.status ?? 0;
      if (status >= 200 && status < 300) {
        lastSyncedTokenKey = tokenKey;
        console.info('[push] token registered', provider, 'status', status);
        return true;
      }
      console.warn('[push] token upload non-2xx', provider, status, 'attempt', attempt);
    } catch (error) {
      console.warn('[push] token upload failed', provider, 'attempt', attempt, error);
    }
    if (attempt < maxAttempts) {
      await sleep(400 * attempt);
    }
  }
  return false;
}

async function onFcmRegistration(token: Token): Promise<void> {
  if (!token.value) return;
  await sendToken('FCM', token.value);
}

async function registerFcmPush(): Promise<void> {
  if (!fcmListenersAttached) {
    fcmListenersAttached = true;
    await PushNotifications.addListener('registration', onFcmRegistration);
    await PushNotifications.addListener('registrationError', (error) => {
      console.warn('[push] FCM registration failed', error);
    });
  }

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') {
    console.warn('[push] Android notification permission not granted', permission);
    return;
  }
  await PushNotifications.register();
}

async function registerIosFcmPush(): Promise<void> {
  if (!iosFcmListenersAttached) {
    iosFcmListenersAttached = true;
    await FcmPush.addListener('tokenRefresh', async ({ token }) => {
      if (token) {
        await sendToken('FCM', token);
      }
    });
  }

  try {
    const { token } = await FcmPush.register();
    if (token) {
      await sendToken('FCM', token);
    } else {
      console.warn('[push] iOS FCM returned empty token (permission denied or APNs not ready)');
    }
  } catch (error) {
    console.warn('[push] iOS FCM registration failed', error);
  }
}

async function registerHmsPush(): Promise<void> {
  if (!hmsListenersAttached) {
    hmsListenersAttached = true;
    await HmsPush.addListener('tokenRefresh', async ({ token }) => {
      if (token) {
        await sendToken('HMS', token);
      }
    });
  }

  try {
    const { token } = await HmsPush.register();
    if (token) {
      await sendToken('HMS', token);
    } else {
      console.warn('[push] HMS returned empty token');
    }
  } catch (error) {
    console.warn('[push] HMS registration failed', error);
  }
}

/**
 * Register native push token and upload to api/account/push-device.
 * Safe to call repeatedly (login, account restore, app resume).
 */
export async function registerAdminPushNotifications(authorities?: unknown): Promise<void> {
  void authorities;
  if (!Capacitor.isNativePlatform()) return;

  if (registerInFlight) {
    await registerInFlight;
    return;
  }

  registerInFlight = (async () => {
    await ensureMessengerPushOpenHandlers();
    flushPendingPushOpen();

    if (Capacitor.getPlatform() === 'ios') {
      await registerIosFcmPush();
      return;
    }

    if (Capacitor.getPlatform() === 'android') {
      const { provider } = await HmsPush.getPushProvider();
      if (provider === 'HMS') {
        await registerHmsPush();
      } else {
        await registerFcmPush();
      }
    }
  })();

  try {
    await registerInFlight;
  } finally {
    registerInFlight = null;
  }
}

/** Drop session sync marker after logout so next login re-uploads. */
export function clearPushTokenSyncState(): void {
  lastSyncedTokenKey = null;
  pendingOpen = null;
  void clearAppIconBadge();
}
